import { Program, PersonalScheduleProgram } from '@/domain'
import { datalakeApiClient } from '@/lib/api/lib/client'
import { mergePersonalScheduleItems } from '@/lib/programmeSnapshots'
import { loadAllProgrammeProgressItems } from '@/lib/programmeSnapshotLoader'

/** JSON payload stored in a p.ref datalake item (written by the backend orchestrator). */
export interface ProgrammeRefData {
    coachUserId: string
    /** Empty for purchased programmes. */
    requestId: string
    sharedAt: string
    programmeName: string
}

/** Result of resolving a programme from datalake (local or shared). */
export interface ResolvedProgramme {
    /**
     * The programme to show. For a started shared programme this is the
     * version the enrollment was built from, so a coach's later edits do not
     * change a running schedule.
     */
    ps: Program | undefined
    /** The latest published version, present when it differs from `ps`. */
    latest?: Program
    pps: PersonalScheduleProgram
    updateAvailable?: boolean
}

async function fetchProgrammeDatalakeItems(
    programId: string,
    kinds: string[],
    items: number
) {
    return datalakeApiClient.getDatalakeItems({
        kind: kinds,
        corellationId: programId,
        items,
        skip: 0
    })
}

// Datalake payloads are user-synced JSON; a corrupted item must degrade
// (fall back to the next resolution path) rather than crash the view.
function safeJsonParse<T>(raw: string, context: string): T | undefined {
    try {
        return JSON.parse(raw) as T
    } catch (error) {
        console.error(`Failed to parse ${context}:`, error)
        return undefined
    }
}

function findDatalakeItemData(
    items: Array<{ id?: string; kind?: string; data?: string }> | undefined,
    kind: string
): string | undefined {
    return items?.find((item) => item.id !== '' && item.kind === kind)?.data
}

// hasProgrammeRef: the items hold a readable p.ref, so the user may fetch
// the programme's published snapshots.
function hasProgrammeRef(
    items: Parameters<typeof findDatalakeItemData>[0]
): boolean {
    const refData = findDatalakeItemData(items, 'p.ref')
    return !!refData && !!safeJsonParse<ProgrammeRefData>(refData, 'p.ref data')
}

/**
 * A published version of a programme the user holds a p.ref to (coach
 * delivery or purchase); the latest one without contentVersion. Read as plain
 * JSON because the generated V1RevealedProgramme mapper drops contentVersion,
 * which update detection depends on.
 */
export async function fetchSharedProgramme(
    programId: string,
    contentVersion?: number
): Promise<Program> {
    const response = await datalakeApiClient.getSharedProgrammeRaw({
        id: programId,
        contentVersion
    })
    const revealed = (await response.raw.json()) as Partial<Program>
    return {
        ...revealed,
        id: programId,
        originalProgramId: programId,
        name: revealed.name || '',
        lessons: revealed.lessons || [],
        schedule: revealed.schedule || []
    } as Program
}

/**
 * Fetches programme data from the user's datalake. Handles both local
 * programmes (owned p.s) and shared programmes (p.ref, delivered by a coach
 * or bought): a started shared programme keeps serving the version its
 * enrollment started from until the user syncs to the latest one.
 */
export async function resolveProgramme(
    programId: string
): Promise<ResolvedProgramme> {
    const [data, progressSnapshots] = await Promise.all([
        fetchProgrammeDatalakeItems(programId, ['p.s', 'p.ps', 'p.ref'], 3),
        loadAllProgrammeProgressItems({ corellationId: programId })
    ])

    const pps =
        mergePersonalScheduleItems([
            ...(data?.items || []),
            ...progressSnapshots
        ]) || ({} as PersonalScheduleProgram)

    const psData = findDatalakeItemData(data?.items, 'p.s')
    if (psData) {
        const ps = safeJsonParse<Program>(psData, 'p.s data')
        if (ps) return { ps, pps }
    }

    if (!hasProgrammeRef(data?.items)) {
        return { ps: undefined, pps }
    }

    const latest = await fetchSharedProgramme(programId)
    const pinned =
        pps.status === 'started' ? pps.sourceContentVersion : undefined
    const updateAvailable =
        pinned != null &&
        latest.contentVersion != null &&
        latest.contentVersion > pinned
    if (!updateAvailable) {
        return { ps: latest, pps, updateAvailable: false }
    }

    const ps = await fetchSharedProgramme(programId, pinned)
    return { ps, latest, pps, updateAvailable }
}

/**
 * Resolves just the programme source (Program) by ID. Used in the start
 * action where p.ps is not needed, so a shared programme resolves to its
 * latest published version. Tries local p.s first, falls back to p.ref.
 */
export async function resolveProgrammeSource(
    programId: string
): Promise<Program | undefined> {
    const data = await fetchProgrammeDatalakeItems(
        programId,
        ['p.s', 'p.ref'],
        2
    )

    const psData = findDatalakeItemData(data?.items, 'p.s')
    if (psData) {
        const ps = safeJsonParse<Program>(psData, 'p.s data')
        if (ps) return ps
    }

    return hasProgrammeRef(data?.items)
        ? fetchSharedProgramme(programId)
        : undefined
}
